/**
 * Reusable list-view pagination.
 *
 * Each table view calls slicePage(key, filteredRows) to render only the visible
 * page, and paginationControlsHtml(key, totalRows) to drop the controls below
 * the table. State (page + perPage) persists per key in localStorage so users
 * keep their preferred page size as they navigate the app.
 *
 * Why pagination instead of virtualization: every view renders its table by
 * concatenating row HTML and stuffing it into innerHTML. Virtualization would
 * require restructuring all of those views to manage scroll offsets and
 * visible windows. Pagination caps DOM size at a tunable maximum without
 * touching the render pipeline.
 */

interface PagState { page: number; perPage: number; }

const KEY_PREFIX = "enigma_pag_v1_";
const state = new Map<string, PagState>();

function load(key: string, defaultPerPage: number): PagState {
  if (state.has(key)) return state.get(key)!;
  let s: PagState;
  try {
    const raw = localStorage.getItem(KEY_PREFIX + key);
    s = raw ? JSON.parse(raw) : { page: 1, perPage: defaultPerPage };
  } catch {
    s = { page: 1, perPage: defaultPerPage };
  }
  if (typeof s.perPage !== "number") s.perPage = defaultPerPage;
  if (typeof s.page !== "number" || s.page < 1) s.page = 1;
  state.set(key, s);
  return s;
}

function persist(key: string): void {
  const s = state.get(key);
  if (!s) return;
  try { localStorage.setItem(KEY_PREFIX + key, JSON.stringify(s)); } catch { /* ignore */ }
}

/** Return the slice of rows that belong on the current page. Auto-clamps the
 *  page number when filtering has reduced the row count below the saved page. */
export function slicePage<T>(key: string, rows: T[], defaultPerPage = 50): T[] {
  const s = load(key, defaultPerPage);
  if (s.perPage <= 0) return rows;  // "All"
  const maxPage = Math.max(1, Math.ceil(rows.length / s.perPage));
  if (s.page > maxPage) s.page = maxPage;
  const start = (s.page - 1) * s.perPage;
  return rows.slice(start, start + s.perPage);
}

/** Render the controls strip. Returns "" when there are no rows. */
export function paginationControlsHtml(key: string, totalRows: number, defaultPerPage = 50): string {
  if (totalRows === 0) return "";
  const s = load(key, defaultPerPage);
  const perPage = s.perPage > 0 ? s.perPage : totalRows;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
  const page = Math.min(s.page, totalPages);
  const startIdx = (page - 1) * perPage + 1;
  const endIdx = Math.min(page * perPage, totalRows);

  const sizes: Array<[number, string]> = [[50, "50"], [100, "100"], [200, "200"], [-1, "All"]];
  const sizeButtons = sizes.map(([n, label]) => {
    const active = (s.perPage === n);
    return `<button class="btn ${active ? "btn-primary" : "btn-ghost"} !py-0.5 !px-2 text-xs" onclick="paginationSetPerPage('${key}', ${n})">${label}</button>`;
  }).join("");

  const prevDisabled = page <= 1;
  const nextDisabled = page >= totalPages;
  return `
    <div class="flex flex-wrap items-center gap-2 text-xs text-[var(--muted)] mt-2 mb-1 px-2">
      <span>Showing <b class="text-white">${startIdx}–${endIdx}</b> of <b class="text-white">${totalRows}</b></span>
      <span class="ml-auto flex items-center gap-1">
        <button class="btn btn-ghost !py-0.5 !px-2 text-xs" ${prevDisabled ? "disabled" : ""} onclick="paginationPrev('${key}')" title="Previous page">‹ Prev</button>
        <span class="px-1">Page <b class="text-white">${page}</b> of <b class="text-white">${totalPages}</b></span>
        <button class="btn btn-ghost !py-0.5 !px-2 text-xs" ${nextDisabled ? "disabled" : ""} onclick="paginationNext('${key}')" title="Next page">Next ›</button>
      </span>
      <span class="flex items-center gap-1 ml-2">
        <span class="text-[var(--muted)] mr-1">Per page:</span>
        ${sizeButtons}
      </span>
    </div>`;
}

/** Reset a key's pagination back to page 1 (e.g. after a filter that should
 *  put the user at the top of the new result set). Most callers don't need
 *  this since slicePage auto-clamps. */
export function resetPaginationPage(key: string): void {
  const s = state.get(key);
  if (!s) return;
  if (s.page !== 1) { s.page = 1; persist(key); }
}

/* ----- Window bridges for inline onclick handlers ----- */
(window as any).paginationPrev = (key: string) => {
  const s = state.get(key);
  if (!s || s.page <= 1) return;
  s.page--;
  persist(key);
  (window as any).render?.();
};
(window as any).paginationNext = (key: string) => {
  const s = state.get(key);
  if (!s) return;
  s.page++;
  persist(key);
  (window as any).render?.();
};
(window as any).paginationSetPerPage = (key: string, n: number) => {
  const s = state.get(key);
  if (!s) return;
  s.perPage = n;
  s.page = 1;
  persist(key);
  (window as any).render?.();
};
